/**
 * JWT Utilities for Dominion Mandate Platform
 *
 * Safe JWT decoding and validation helpers for extracting role information
 * from Supabase JWT tokens.
 */

export interface JWTPayload {
    sub?: string;
    email?: string;
    exp?: number;
    iat?: number;
    aud?: string;
    app_metadata?: {
        role?: string;
        roleId?: number;
        provider?: string;
        [key: string]: unknown;
    };
    user_metadata?: {
        name?: string;
        avatar_url?: string;
        [key: string]: unknown;
    };
}

export interface DecodedRole {
    name: string;
    id: number;
}

/**
 * Safely decode a JWT token without verification
 * @param token - JWT token string
 * @returns Decoded payload or null if invalid
 */
export function decodeJWT(token: string): JWTPayload | null {
    try {
        // JWT structure: header.payload.signature
        const parts = token.split('.');
        if (parts.length !== 3) {
            console.warn('[JWT Utils] Invalid JWT format');
            return null;
        }

        // Decode the payload (second part)
        const payload = parts[1];

        // Add padding if needed
        const paddedPayload = payload.padEnd(payload.length + (4 - (payload.length % 4)) % 4, '=');

        // Decode base64url to base64
        const base64Payload = paddedPayload.replace(/-/g, '+').replace(/_/g, '/');

        // Parse JSON
        const decoded = JSON.parse(atob(base64Payload));

        return decoded as JWTPayload;
    } catch (error) {
        console.error('[JWT Utils] Failed to decode JWT:', error);
        return null;
    }
}

/**
 * Extract role information from JWT payload
 * @param token - JWT token string
 * @returns Role information or default member role
 */
export function extractRoleFromJWT(token: string): DecodedRole {
    const payload = decodeJWT(token);

    // DEBUG: Log full payload structure for diagnostics
    if (process.env.NODE_ENV !== 'production') {
        console.debug('[JWT Utils] Full JWT payload:', payload);
        console.debug('[JWT Utils] app_metadata:', payload?.app_metadata);
        console.debug('[JWT Utils] role value:', payload?.app_metadata?.role);
    }

    if (!payload?.app_metadata) {
        console.warn('[JWT Utils] No app_metadata in JWT - this means Supabase JWT does not include custom claims');
        console.warn('[JWT Utils] To fix: Configure Supabase JWT settings to include app_metadata.role claim');
        return { name: 'member', id: 1 };
    }

    const { role, roleId } = payload.app_metadata;

    // Validate role name
    const validRoles = ['member', 'pastor', 'media_team', 'admin', 'moderator'];
    const roleName = (typeof role === 'string' && validRoles.includes(role))
        ? role
        : 'member';

    // Log if role was not found or invalid
    if (!role) {
        console.warn('[JWT Utils] Role claim missing from app_metadata. User will be treated as member.');
        console.warn('[JWT Utils] Solution: Set user role in Supabase > Users > app_metadata, then user must logout/login');
    } else if (typeof role !== 'string' || !validRoles.includes(role)) {
        console.warn(`[JWT Utils] Invalid role value: "${role}". Valid roles are: ${validRoles.join(', ')}`);
    } else {
        console.debug(`[JWT Utils] Role extracted successfully: ${role}`);
    }

    // Validate role ID
    const roleIdNum = (typeof roleId === 'number' && roleId >= 1 && roleId <= 5)
        ? roleId
        : 1; // Default to member

    return {
        name: roleName,
        id: roleIdNum
    };
}

/**
 * Check if a JWT token is expired
 * @param token - JWT token string
 * @returns true if expired, false otherwise
 */
export function isJWTExpired(token: string): boolean {
    const payload = decodeJWT(token);

    if (!payload?.exp) {
        return true; // Consider invalid tokens as expired
    }

    // exp is in seconds, Date.now() is in milliseconds
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now;
}

/**
 * Validate JWT token structure and basic claims
 * @param token - JWT token string
 * @returns true if valid structure, false otherwise
 */
export function validateJWTStructure(token: string): boolean {
    if (!token || typeof token !== 'string') {
        return false;
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
        return false;
    }

    // Check if all parts are non-empty
    return parts.every(part => part.length > 0);
}

/**
 * Get user information from JWT token
 * @param token - JWT token string
 * @returns User information extracted from JWT
 */
export function getUserFromJWT(token: string) {
    const payload = decodeJWT(token);

    if (!payload) {
        return null;
    }

    return {
        id: payload.sub || '',
        email: payload.email || '',
        name: payload.user_metadata?.name || payload.email?.split('@')[0] || 'User',
        role: extractRoleFromJWT(token),
        profilePictureUrl: payload.user_metadata?.avatar_url || null,
        authProvider: payload.app_metadata?.provider || 'email',
        emailVerified: true, // Supabase handles email verification
        createdAt: payload.iat ? new Date(payload.iat * 1000).toISOString() : new Date().toISOString(),
        updatedAt: new Date().toISOString()
    };
}

/**
 * Check if token needs refresh (expires within 5 minutes)
 * @param token - JWT token string
 * @returns true if token should be refreshed
 */
export function shouldRefreshToken(token: string): boolean {
    const payload = decodeJWT(token);

    if (!payload?.exp) {
        return true;
    }

    const now = Math.floor(Date.now() / 1000);
    const fiveMinutesFromNow = now + (5 * 60);

    return payload.exp < fiveMinutesFromNow;
}